import type { ModelLatest, ModelStep } from "../api/types";
import { formatApprox, formatDuration } from "../ui/format";

const STEPS: { key: ModelStep; label: string }[] = [
  { key: "queued", label: "Waiting to start" },
  { key: "thinking", label: "Working out the shape" },
  { key: "building", label: "Building it" },
  { key: "checking", label: "Checking the result" },
];

const ICON = { done: "✓", active: "●", pending: "○" } as const;

/** A step that is built without an instruction: a tuning change, a restored
 *  step being rebuilt, a design started from an object. */
export function isBuild(latest: Pick<ModelLatest, "source">): boolean {
  return (
    latest.source === "params" ||
    latest.source === "restore" ||
    latest.source === "remix"
  );
}

/** Where the step in progress stands ([design-studio §11.5]): the four steps
 *  of an instruction in the job page's step markup, the elapsed time on the
 *  active one, and beneath them when it should be ready. A build shows only
 *  "Building it". Rendered only while `latest` is queued or running. */
export function StateLine(props: {
  latest: ModelLatest;
  /** What an instruction like this one usually takes. */
  estimate: { credits: number; seconds: number };
  now: number;
  /** When the step was created; the estimate counts from there. */
  createdAt?: string | null;
  /** Said beneath the steps in place of when it should be ready: a
   *  generation of a sculpted design has no estimate ([sculpted §5.2]). */
  note?: string;
}): JSX.Element | null {
  const { latest, estimate, now } = props;
  if (latest.status !== "queued" && latest.status !== "running") return null;
  const build = isBuild(latest);
  const steps = build ? STEPS.filter((s) => s.key === "building") : STEPS;
  const at = build ? 0 : steps.findIndex((s) => s.key === latest.step);
  const started = latest.step_started_at
    ? Date.parse(latest.step_started_at)
    : NaN;
  const elapsed = Number.isNaN(started) ? null : (now - started) / 1000;

  const created = props.createdAt ? Date.parse(props.createdAt) : NaN;
  const since = Number.isNaN(created)
    ? (elapsed ?? 0)
    : Math.max(0, (now - created) / 1000);
  const left = estimate.seconds - since;

  return (
    <div className="flex flex-col gap-2" data-testid="state-line">
      <ol className="m-0 flex list-none flex-col gap-1 p-0">
        {steps.map((step, index) => {
          // A step past "checking" (`done`) leaves every row done.
          const status =
            at === -1 || index < at
              ? "done"
              : index === at
                ? "active"
                : "pending";
          return (
            <li
              key={step.key}
              data-testid={`step-${step.key}`}
              data-status={status}
              aria-current={status === "active" ? "step" : undefined}
              className={`flex items-baseline gap-2 text-sm ${
                status === "pending" ? "text-slate-400" : "text-slate-800"
              } ${status === "active" ? "font-semibold" : ""}`}
            >
              <span
                aria-hidden="true"
                className={`w-4 text-center ${
                  status === "done"
                    ? "text-brand"
                    : status === "active"
                      ? "animate-pulse text-brand"
                      : ""
                }`}
              >
                {ICON[status]}
              </span>
              <span className="flex-1">{step.label}</span>
              <span className="text-xs font-normal tabular-nums text-slate-500">
                {status === "active" && elapsed !== null
                  ? formatDuration(elapsed)
                  : ""}
              </span>
            </li>
          );
        })}
      </ol>
      {build ? null : (
        <p
          role="status"
          className="m-0 text-xs text-slate-600"
          data-testid="ready-in"
        >
          {props.note ??
            (left > 0
              ? `Ready in ${formatApprox(Math.max(5, left))}.`
              : "Taking longer than usual.")}
        </p>
      )}
    </div>
  );
}
